<script setup lang="ts">
// Second layer of the cookie banner: one toggle per category.
//
// Optional categories start OFF unless the visitor already chose otherwise
// (opt-in, never pre-ticked: CJEU Planet49, C-673/17). "Necessary" is shown as
// always on and cannot be toggled. The three footer actions share one class
// constant, so reject-all is exactly as prominent as accept-all.
//
// Mounted only while open (see CookieConsent.vue), so it owns its keyboard
// handling for exactly that lifetime: focus trap, Escape to close, body scroll
// lock, and focus restored to where the visitor came from.
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { X } from 'lucide-vue-next'
import {
  COOKIE_CATEGORY_COPY,
  COOKIE_CATEGORY_ORDER,
  listCookieItems,
  toCookieLocale,
} from '@/content/cookies/cookieInventory'
import { useEscapeToClose } from '@/composables/useEscapeToClose'
import { useFocusTrap } from '@/composables/useFocusTrap'
import { NO_CONSENT } from '@/services/consentRecord'
import { useConsentStore } from '@/stores/consentStore'
import type { ConsentCategory, ConsentChoices, OptionalConsentCategory } from '@/types/consent'
import { CONSENT_ACTION_BAR_CLASS, CONSENT_ACTION_CLASS } from './consentStyles'

const { t, locale } = useI18n()
const store = useConsentStore()

const dialogRef = ref<HTMLElement | null>(null)
const lang = computed(() => toCookieLocale(String(locale.value)))
const cookiePolicyHref = computed(() =>
  lang.value === 'es' ? '/cookies' : `/${lang.value}/cookies`,
)

// Working copy: nothing is stored until the visitor presses a footer action.
const draft = reactive<ConsentChoices>({ ...(store.activeChoices ?? NO_CONSENT) })
const expanded = reactive<Record<string, boolean>>({})

const categories = computed(() =>
  COOKIE_CATEGORY_ORDER.map((id) => ({
    id,
    title: COOKIE_CATEGORY_COPY[id].title[lang.value],
    description: COOKIE_CATEGORY_COPY[id].description[lang.value],
    empty: COOKIE_CATEGORY_COPY[id].empty[lang.value],
    items: listCookieItems(id),
  })),
)

function isOn(id: ConsentCategory): boolean {
  return id === 'necessary' ? true : draft[id]
}

function toggle(id: ConsentCategory): void {
  if (id === 'necessary') return
  draft[id as OptionalConsentCategory] = !draft[id as OptionalConsentCategory]
}

function stateLabel(id: ConsentCategory): string {
  if (id === 'necessary') return t('components.cookieConsent.modal.alwaysOn')
  return isOn(id)
    ? t('components.cookieConsent.modal.enabled')
    : t('components.cookieConsent.modal.disabled')
}

function itemsLabel(count: number): string {
  return count === 0
    ? t('components.cookieConsent.modal.inUseNone')
    : t('components.cookieConsent.modal.inUse', { count })
}

function close(): void {
  store.closePreferences()
}

useEscapeToClose(() => true, close)
useFocusTrap(dialogRef, () => true)

// Remember where the visitor came from, lock page scroll while open.
//
// When opened from the banner, the banner is already unmounted by the time this
// runs (CookieConsent hides it while the modal is open), so the focused button
// has been destroyed and document.activeElement is <body>. <body> is not a real
// place to return to, so it counts as "no opener".
const active = document.activeElement as HTMLElement | null
const opener = active && active !== document.body ? active : null
const previousOverflow = document.body.style.overflow
onMounted(() => {
  document.body.style.overflow = 'hidden'
})
onBeforeUnmount(() => {
  document.body.style.overflow = previousOverflow
  // No usable opener (see above): fall back to whichever re-entry point is on
  // screen once this closes - the floating button after a decision, the banner's
  // first button if the visitor closed without deciding.
  void nextTick(() => {
    const target = opener?.isConnected
      ? opener
      : document.querySelector<HTMLElement>('[data-cookie-return-focus]')
    target?.focus()
  })
})
</script>

<template>
  <div
    class="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
    data-testid="cookie-modal-backdrop"
    @click.self="close"
  >
    <div
      ref="dialogRef"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tpa-cookie-modal-title"
      aria-describedby="tpa-cookie-modal-intro"
      tabindex="-1"
      class="flex max-h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[15px] bg-white shadow-2xl sm:rounded-[15px]"
      data-testid="cookie-modal"
    >
      <header class="flex items-start justify-between gap-4 px-5 pb-2 pt-5 sm:px-6">
        <div>
          <h2 id="tpa-cookie-modal-title" class="text-lg font-bold! text-smartway-blue">
            {{ t('components.cookieConsent.modal.title') }}
          </h2>
          <p
            id="tpa-cookie-modal-intro"
            class="mt-1 text-sm leading-relaxed text-[var(--color-ink)]"
          >
            {{ t('components.cookieConsent.modal.intro') }}
          </p>
        </div>
        <button
          type="button"
          class="-mr-2 -mt-1 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-[var(--color-ink)] hover:bg-[var(--color-cool-50)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-smartway-blue"
          :aria-label="t('components.cookieConsent.modal.close')"
          data-testid="cookie-modal-close"
          @click="close"
        >
          <X :size="22" aria-hidden="true" />
        </button>
      </header>

      <ul class="flex-1 divide-y divide-[var(--color-cool-200)] overflow-y-auto px-5 sm:px-6">
        <li
          v-for="category in categories"
          :key="category.id"
          class="py-4"
          :data-testid="`cookie-category-${category.id}`"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <h3
                :id="`tpa-cookie-cat-${category.id}`"
                class="font-semibold! text-[var(--color-ink)]"
              >
                {{ category.title }}
                <span class="ml-1 text-xs font-normal text-[var(--color-muted)]">
                  ({{ itemsLabel(category.items.length) }})
                </span>
              </h3>
              <p
                :id="`tpa-cookie-cat-${category.id}-desc`"
                class="mt-1 text-sm leading-relaxed text-[var(--color-ink-soft)]"
              >
                {{ category.description }}
              </p>
            </div>

            <div class="flex shrink-0 flex-col items-end gap-1">
              <!-- aria-disabled (not disabled) on the locked one: it stays focusable and is announced as "on". -->
              <button
                type="button"
                role="switch"
                :aria-checked="isOn(category.id)"
                :aria-disabled="category.id === 'necessary' ? 'true' : undefined"
                :aria-labelledby="`tpa-cookie-cat-${category.id}`"
                :aria-describedby="`tpa-cookie-cat-${category.id}-desc`"
                class="relative h-6 w-11 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-smartway-blue"
                :class="[
                  isOn(category.id) ? 'bg-smartway-blue' : 'bg-slate-500',
                  category.id === 'necessary' ? 'cursor-not-allowed' : 'cursor-pointer',
                ]"
                :data-testid="`cookie-toggle-${category.id}`"
                @click="toggle(category.id)"
              >
                <span
                  class="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
                  :class="isOn(category.id) ? 'translate-x-5' : 'translate-x-0'"
                />
              </button>
              <!-- Text as well as colour: the state is never conveyed by colour alone. -->
              <span class="text-xs font-medium! text-[var(--color-ink-soft)]" aria-hidden="true">
                {{ stateLabel(category.id) }}
              </span>
            </div>
          </div>

          <button
            type="button"
            class="mt-2 cursor-pointer text-sm font-semibold! text-smartway-blue underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-smartway-blue"
            :aria-expanded="expanded[category.id] === true"
            :aria-controls="`tpa-cookie-cat-${category.id}-items`"
            @click="expanded[category.id] = !expanded[category.id]"
          >
            {{
              expanded[category.id]
                ? t('components.cookieConsent.modal.hideDetails')
                : t('components.cookieConsent.modal.showDetails')
            }}
          </button>

          <div
            v-if="expanded[category.id]"
            :id="`tpa-cookie-cat-${category.id}-items`"
            class="mt-3"
          >
            <p v-if="category.items.length === 0" class="text-sm text-[var(--color-ink-soft)]">
              {{ category.empty }}
            </p>
            <ul v-else class="space-y-3">
              <li
                v-for="item in category.items"
                :key="item.id"
                class="rounded-lg bg-[var(--color-cool-25)] p-3 text-sm"
              >
                <p class="flex flex-wrap gap-1.5">
                  <code
                    v-for="name in item.names"
                    :key="name"
                    class="rounded bg-white px-1.5 py-0.5 text-xs text-[var(--color-ink)] ring-1 ring-[var(--color-cool-200)]"
                  >
                    {{ name }}
                  </code>
                </p>
                <p class="mt-2 text-[var(--color-ink)]">{{ item.purpose[lang] }}</p>
                <dl
                  class="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-[var(--color-ink-soft)]"
                >
                  <dt class="font-semibold!">{{ t('components.cookieConsent.item.type') }}</dt>
                  <dd>{{ t(`components.cookieConsent.item.kind.${item.kind}`) }}</dd>
                  <dt class="font-semibold!">{{ t('components.cookieConsent.item.provider') }}</dt>
                  <dd>{{ item.provider }}</dd>
                  <dt class="font-semibold!">{{ t('components.cookieConsent.item.duration') }}</dt>
                  <dd>{{ item.duration[lang] }}</dd>
                </dl>
              </li>
            </ul>
          </div>
        </li>
      </ul>

      <div class="px-5 pb-3 pt-2 text-sm sm:px-6">
        <a
          :href="cookiePolicyHref"
          target="_blank"
          rel="noopener noreferrer"
          class="font-semibold! text-smartway-blue underline underline-offset-2"
        >
          {{ t('components.cookieConsent.modal.policyLink') }}
        </a>
      </div>

      <div :class="CONSENT_ACTION_BAR_CLASS">
        <button
          type="button"
          :class="CONSENT_ACTION_CLASS"
          data-testid="cookie-modal-reject-all"
          @click="store.rejectAll()"
        >
          {{ t('components.cookieConsent.modal.rejectAll') }}
        </button>
        <button
          type="button"
          :class="CONSENT_ACTION_CLASS"
          data-testid="cookie-modal-save"
          @click="store.saveConsent({ ...draft })"
        >
          {{ t('components.cookieConsent.modal.save') }}
        </button>
        <button
          type="button"
          :class="CONSENT_ACTION_CLASS"
          data-testid="cookie-modal-accept-all"
          @click="store.acceptAll()"
        >
          {{ t('components.cookieConsent.modal.acceptAll') }}
        </button>
      </div>
    </div>
  </div>
</template>
